/**
 * Retention message type definitions shared across the panel UI.
 *
 * Only Apple-reviewed types live as retention messages. Switch_plan and
 * promotional_offer realtime response shapes are now driven by the
 * `realtimeRetentionMessage` backend parameter in config-api (see
 * retention-messaging-service/CLAUDE.md). They no longer appear in this
 * picker.
 *
 * Mirror of config-manager/api/middlewares/retentionValidation.js SUPPORTED_MESSAGE_TYPES.
 * Keep both in sync.
 */

export const MESSAGE_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BULLETED: 'bulleted',
  BULLETED_IMAGE: 'bulleted_image'
};

export const TYPES_WITH_MAIN_IMAGE = new Set([
  MESSAGE_TYPES.IMAGE,
  MESSAGE_TYPES.BULLETED_IMAGE
]);

export const TYPES_WITH_BULLETS = new Set([
  MESSAGE_TYPES.BULLETED,
  MESSAGE_TYPES.BULLETED_IMAGE
]);

// Apple character limits — single source of truth for live-counter UIs.
export const HEADER_MAX = 66;
export const BODY_MAX = 144;
export const ALT_TEXT_MAX = 150;
export const BULLET_TEXT_MAX = 66;
export const BULLET_ALT_TEXT_MAX = 150;
export const MAX_BULLETS = 5;

// Image `usage` is required at upload and immutable afterwards. The two values
// map to Apple's two physical formats:
//   - 'main'   → FULL_SIZE (3840 wide, h ∈ [160, 2160])
//   - 'bullet' → BULLET_POINT (1024 × 1024)
// Legacy images uploaded before this field existed are physically FULL_SIZE
// and are surfaced as 'main' on read (see resolveImageUsage helper).
export const IMAGE_USAGES = ['main', 'bullet'];

export const IMAGE_USAGE_DIMENSIONS = {
  main: '3840 × [160–2160] px',
  bullet: '1024 × 1024 px'
};

// Legacy images (no `usage` field) were uploaded as 3840-wide FULL_SIZE.
// Surface them as 'main' so they only appear in the main-image picker.
export const resolveImageUsage = (img) => (img?.usage === 'bullet' ? 'bullet' : 'main');

export const HEADER_POSITIONS = {
  ABOVE_BODY: 'ABOVE_BODY',
  ABOVE_IMAGE: 'ABOVE_IMAGE'
};

/**
 * Display metadata for the type-picker modal cards and chip badges.
 */
export const MESSAGE_TYPE_META = [
  {
    type: MESSAGE_TYPES.TEXT,
    label: 'Text',
    short: 'Text',
    icon: 'fas fa-font',
    summary: 'Header + body, no media.',
    description: 'A simple text-only retention message. Best for clear, direct copy.',
    chipColor: 'grey',
    available: true
  },
  {
    type: MESSAGE_TYPES.IMAGE,
    label: 'Image',
    short: 'Image',
    icon: 'fas fa-image',
    summary: 'Header + body + a hero image with alt text.',
    description: 'Adds a single image above or below the header. Apple reviews each image separately.',
    chipColor: 'blue',
    available: true
  },
  {
    type: MESSAGE_TYPES.BULLETED,
    label: 'Bulleted',
    short: 'Bulleted',
    icon: 'fas fa-list-ul',
    summary: 'Header + body + 1–5 bullets, each with an icon.',
    description: 'Use to highlight features or benefits. Each bullet has its own small image and text.',
    chipColor: 'indigo',
    available: true
  },
  {
    type: MESSAGE_TYPES.BULLETED_IMAGE,
    label: 'Bulleted + Image',
    short: 'Bulleted+Image',
    icon: 'fas fa-stream',
    summary: 'Hero image + header + body + bullets.',
    description: 'Combines a main hero image with a 1–5 bullet list. Position the header above the image or body.',
    chipColor: 'indigo',
    available: true
  }
];

export const META_BY_TYPE = MESSAGE_TYPE_META.reduce((acc, m) => {
  acc[m.type] = m;
  return acc;
}, {});
